/**
 * Lampe de poche.
 *
 * La nuit est volontairement tres sombre (c'est le moment des feux
 * d'artifice), donc il faut de quoi s'eclairer pour continuer a explorer. Un
 * seul `SpotLight` porte par le joueur, oriente vers l'avant et vers le bas,
 * plus un halo au sol pour que le faisceau se lise depuis la vue a 45 degres.
 *
 * La lumiere est creee UNE FOIS et reste dans la scene, intensite a zero quand
 * elle est eteinte: ajouter ou retirer une lumiere change le nombre de lumieres
 * de la scene, ce qui force three.js a recompiler tous les shaders — et ca se
 * voit a l'ecran sous forme de saccade.
 */
import * as THREE from 'three'
import { clamp } from '../../shared/sim/alea.ts'

/**
 * Intensite du faisceau allume. Montee de 3,2 a 4,6 a la demande du
 * proprietaire: la nuit restait trop fermee pour explorer a la lampe.
 */
const INTENSITE = 4.6
/** Portee du faisceau, en metres. Allongee de 26 a 36: on voit plus loin. */
const PORTEE = 36
/** Vitesse d'allumage et d'extinction, en unites d'intensite par seconde. */
const VITESSE = 9

export class Lampe {
  readonly faisceau: THREE.SpotLight
  private cible: THREE.Object3D
  private halo: THREE.Mesh
  private scene: THREE.Scene
  private allumee = false
  private intensite = 0
  private materiauHalo: THREE.MeshBasicMaterial

  constructor(scene: THREE.Scene) {
    this.scene = scene

    // L'angle s'ouvre un peu avec la portee (PI/6.5 au lieu de PI/7), sinon le
    // faisceau plus long devient un crayon.
    this.faisceau = new THREE.SpotLight(0xfff0cc, 0, PORTEE, Math.PI / 6.5, 0.45, 1.05)
    this.faisceau.castShadow = false
    this.faisceau.name = 'lampe'
    this.cible = new THREE.Object3D()
    this.faisceau.target = this.cible
    scene.add(this.faisceau, this.cible)

    // Halo au sol: en vue orthographique a 45 degres, le cone lui-meme se voit
    // mal; c'est la tache lumineuse qui fait comprendre qu'on tient une lampe.
    this.materiauHalo = new THREE.MeshBasicMaterial({
      color: 0xffe9b8,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    })
    this.halo = new THREE.Mesh(new THREE.CircleGeometry(4.2, 28), this.materiauHalo)
    this.halo.rotation.x = -Math.PI / 2
    this.halo.renderOrder = 2
    this.halo.visible = false
    scene.add(this.halo)
  }

  get estAllumee(): boolean {
    return this.allumee
  }

  basculer(): boolean {
    this.allumee = !this.allumee
    return this.allumee
  }

  allumer(v: boolean): void {
    this.allumee = v
  }

  /**
   * `x, y, z` est la position des pieds du joueur, `rot` son orientation
   * (0 = vers +z), `facteurNuit` sert a ne pas eblouir en plein jour.
   */
  maj(dt: number, x: number, y: number, z: number, rot: number, facteurNuit: number): void {
    const vise = this.allumee ? INTENSITE : 0
    this.intensite += clamp(vise - this.intensite, -VITESSE * dt, VITESSE * dt)

    // En plein jour la lampe ne sert a rien: on la laisse allumable, mais
    // discrete, pour ne pas delaver le paysage.
    const jour = 0.25 + 0.75 * facteurNuit
    this.faisceau.intensity = this.intensite * jour

    if (this.intensite <= 0.001) {
      this.halo.visible = false
      return
    }

    const dx = Math.sin(rot)
    const dz = Math.cos(rot)

    // Source a hauteur de main, legerement devant.
    this.faisceau.position.set(x + dx * 0.35, y + 1.15, z + dz * 0.35)
    // Cible devant et au sol: le faisceau balaie donc le terrain devant soi.
    // Elle a recule avec la portee (9 -> 13 m), sinon la lumiere plus forte
    // n'eclairait toujours que ses propres pieds.
    this.cible.position.set(x + dx * 13, y - 0.6, z + dz * 13)
    this.cible.updateMatrixWorld()

    this.halo.visible = true
    this.halo.position.set(x + dx * 5.2, y + 0.06, z + dz * 5.2)
    this.materiauHalo.opacity = 0.16 * (this.intensite / INTENSITE) * jour
  }

  dispose(): void {
    this.scene.remove(this.faisceau, this.cible, this.halo)
    this.halo.geometry.dispose()
    this.materiauHalo.dispose()
    this.faisceau.dispose()
  }
}
